/**
 * v135 — Snapshot builders.
 *
 * Adapt the live state from the various surfaces (ArrowBuilder, ActiveSession,
 * RangeView session detail, CompareView) into the share-engine's normalized
 * snapshot shapes. Lives in its own module so the share-button mount sites
 * stay terse and the conversion logic is unit-testable.
 */

import type { BuildState, BuildDerived } from '@/types';
import type { ShotSession } from '@/range/types';
import type {
  ShareBuildSnapshot,
  ShareSessionSnapshot,
  ShareCompareSnapshot,
  ShareShotDot,
  ShareCompareSource,
  ShareAggregates,
} from './types';

/** Build a build-variant snapshot from the live builder state. */
export function buildBuildSnapshot(
  state: BuildState,
  derived: BuildDerived,
  opts: {
    sightTapeDistances?: number[];
    customLabel?: string;
    tagline?: string;
  } = {},
): ShareBuildSnapshot {
  return {
    variant: 'build',
    state,
    derived,
    sightTapeDistances: opts.sightTapeDistances,
    customLabel: opts.customLabel,
    tagline: opts.tagline ?? defaultBuildTagline(derived),
  };
}

/** A short Sal-voice line beneath the marquee stats. */
function defaultBuildTagline(d: BuildDerived): string {
  if (d.foc >= 15 && d.foc <= 18) return 'Heavy FOC, hunter-grade momentum.';
  if (d.foc < 11) return 'Light front end — speed bias.';
  if (d.estimatedFps >= 300) return 'Fast, flat-shooting, ready for the field.';
  if (d.momentum >= 0.50) return 'Bone-breaking momentum for elk and bigger.';
  return 'Built in The Forge. Shot in the field.';
}

/** Flatten a session's shots into the share format. */
export function buildSessionSnapshot(
  session: ShotSession,
  opts: {
    customLabel?: string;
    photoDataUri?: string;
  } = {},
): ShareSessionSnapshot {
  // Pull v3 shots first, fall back to v2 richShots, then v1 simple shots.
  const flatShots = extractShotDots(session);
  const aggregates = computeShareAggregates(flatShots, session.distanceYd);
  return {
    variant: 'session',
    session,
    shots: flatShots,
    aggregates,
    photoDataUri: opts.photoDataUri,
    customLabel: opts.customLabel,
  };
}

/** Pull shot dots out of a session, honoring the v3 sets schema. */
export function extractShotDots(session: ShotSession): ShareShotDot[] {
  if (session.sets && session.sets.length > 0) {
    const out: ShareShotDot[] = [];
    for (const set of session.sets) {
      const setShots = (session.richShots ?? []).filter((s) =>
        set.shotIds.includes(s.id),
      );
      for (const s of setShots) {
        if (typeof s.hitXIn === 'number' && typeof s.hitYIn === 'number') {
          out.push({ xIn: s.hitXIn, yIn: s.hitYIn, sourceKey: set.label ?? set.id });
        }
      }
    }
    return out;
  }
  if (session.richShots && session.richShots.length > 0) {
    return session.richShots
      .filter((s) => typeof s.hitXIn === 'number' && typeof s.hitYIn === 'number')
      .map((s) => ({ xIn: s.hitXIn, yIn: s.hitYIn }));
  }
  if (session.shots && session.shots.length > 0) {
    return session.shots.map((s) => ({ xIn: s.x, yIn: s.y }));
  }
  return [];
}

/**
 * Compare snapshot — given a list of (sessionLabel, color, shots) sources,
 * return a normalized compare snapshot ready to render. The shot dots are
 * tagged with `sourceKey === source.label` so the SVG renderer can color
 * them via lookup.
 */
export function buildCompareSnapshot(
  sources: Array<{
    label: string;
    color: string;
    shots: Array<{ xIn: number; yIn: number }>;
  }>,
  distanceYd: number | null,
  opts: { customLabel?: string } = {},
): ShareCompareSnapshot {
  const allShots: ShareShotDot[] = [];
  const legend: ShareCompareSource[] = [];
  for (const src of sources) {
    const perSourceAggregates = computeShareAggregates(
      src.shots.map((p) => ({ xIn: p.xIn, yIn: p.yIn })),
      distanceYd,
    );
    legend.push({
      label: src.label,
      color: src.color,
      count: src.shots.length,
      meanRadiusIn: perSourceAggregates.meanRadiusIn,
    });
    for (const s of src.shots) {
      allShots.push({ xIn: s.xIn, yIn: s.yIn, sourceKey: src.label });
    }
  }
  return {
    variant: 'compare',
    shots: allShots,
    sources: legend,
    aggregates: computeShareAggregates(allShots, distanceYd),
    customLabel: opts.customLabel,
  };
}

/**
 * Compute the aggregate stats used on session + compare cards.
 *
 * mean radius: average Euclidean distance from POA.
 * 95% group:  2 × stddev of radial offsets from group center.
 */
export function computeShareAggregates(
  shots: ReadonlyArray<{ xIn: number; yIn: number }>,
  distanceYd: number | null,
): ShareAggregates {
  if (shots.length === 0) {
    return {
      count: 0,
      meanRadiusIn: 0,
      group95RadiusIn: 0,
      distanceYd,
    };
  }
  const radii = shots.map((s) => Math.hypot(s.xIn, s.yIn));
  const meanRadius = radii.reduce((a, b) => a + b, 0) / radii.length;
  // Group center
  const cx = shots.reduce((a, b) => a + b.xIn, 0) / shots.length;
  const cy = shots.reduce((a, b) => a + b.yIn, 0) / shots.length;
  const radiiFromCenter = shots.map((s) => Math.hypot(s.xIn - cx, s.yIn - cy));
  const meanFromCenter =
    radiiFromCenter.reduce((a, b) => a + b, 0) / radiiFromCenter.length;
  const variance =
    radiiFromCenter.reduce((a, b) => a + (b - meanFromCenter) ** 2, 0) /
    Math.max(1, radiiFromCenter.length);
  const stddev = Math.sqrt(variance);
  return {
    count: shots.length,
    meanRadiusIn: meanRadius,
    group95RadiusIn: 2 * stddev,
    distanceYd,
  };
}
